import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Wallet, TrendingUp, ShieldCheck,
  UserCheck, FileCheck2, GitBranch, Link2, AlertTriangle, FileSpreadsheet,
  LogOut, Banknote, Coins, KeyRound, BookText
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { roleLabel } from "../lib/format";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/borrowers", label: "Borrower", icon: Users, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/groups", label: "Group Obligor", icon: Building2, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/facilities", label: "Fasilitas Kredit", icon: Wallet, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/exposures", label: "Eksposur", icon: TrendingUp, roles: ["admin", "credit_officer", "risk_officer", "viewer"] },
  { to: "/collaterals", label: "Collateral", icon: Coins, roles: ["admin", "credit_officer", "risk_officer", "viewer"] },
  { to: "/guarantors", label: "Guarantor", icon: ShieldCheck, roles: ["admin", "credit_officer", "risk_officer", "viewer"] },
  { to: "/covenants", label: "Covenant", icon: FileCheck2, roles: ["admin", "credit_officer", "risk_officer", "viewer"] },
  { to: "/approvals", label: "Approval", icon: UserCheck, roles: ["admin", "credit_officer", "approver", "viewer"] },
  { to: "/graph", label: "Graph View", icon: GitBranch, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/connected-obligors", label: "Connected Obligor", icon: Link2, roles: ["admin", "risk_officer", "credit_officer", "viewer"] },
  { to: "/alerts", label: "Early Warning", icon: AlertTriangle, roles: ["admin", "risk_officer", "credit_officer", "viewer"] },
  { to: "/reports", label: "Laporan", icon: FileSpreadsheet, roles: ["admin", "risk_officer", "credit_officer", "approver", "viewer"] },
  { to: "/product-guide", label: "Panduan Produk", icon: BookText, roles: ["admin", "credit_officer", "risk_officer", "approver", "viewer"] },
  { to: "/integrations", label: "Integrasi API", icon: KeyRound, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "viewer";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="w-64 bg-zinc-950 text-zinc-100 flex flex-col" data-testid="sidebar">
        <div className="px-6 py-5 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Banknote className="h-6 w-6 text-white" />
            <span className="text-base font-bold tracking-tight">LIMIT.GRAPH</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            Loan Facility Mgmt
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_ITEMS.filter((i) => i.roles.includes(role) || role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.to.replace("/", "") || "dashboard"}`}
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="text-xs text-zinc-400 mb-1">Login sebagai</div>
          <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500" data-testid="current-user-role">{roleLabel(role)}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-3 w-full flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm py-2 rounded-md transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Keluar
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
